import { get, post } from './api.client';

/**
 * Client for the two-phase email change.
 *
 * The address is NOT changed by the request call — a pending record is created
 * and the new address must prove control by presenting a one-time token. This
 * mirrors the API deliberately: any UI that appears to change the email
 * immediately would misrepresent what actually happened.
 */

export type EmailChangeStatus =
  | { pending: false }
  | {
      pending: true;
      /** Partially masked, e.g. `ne***@ex***.com`. The API never returns it in full. */
      newEmail: string;
      expiresAt: string;
      requestedAt: string;
    };

export type RequestEmailChangeResponse = {
  success: true;
  message: string;
  expiresAt: string;
};

/**
 * Phase 1. Requires the current password — a session alone must never be enough
 * to move the address that password recovery is anchored to.
 */
export async function requestEmailChange(payload: {
  newEmail: string;
  currentPassword: string;
}): Promise<{ data: RequestEmailChangeResponse }> {
  return post<{ data: RequestEmailChangeResponse }>('/users/profile/email/change-request', {
    body: payload,
  });
}

/** Pending-request state, so the UI can show a banner with an expiry countdown. */
export async function getEmailChangeStatus(): Promise<{ data: EmailChangeStatus }> {
  return get<{ data: EmailChangeStatus }>('/users/profile/email/change-status');
}

/**
 * Phase 2. Called from the link in the NEW address's inbox.
 *
 * Public endpoint — the person confirming may be on a device that has never
 * signed in, which is exactly the case this flow exists to serve.
 */
export async function confirmEmailChange(payload: { token: string }) {
  return post<{ data: { success: true; email: string; sessionsRevoked: boolean } }>(
    '/users/profile/email/change-confirm',
    { body: payload }
  );
}

/**
 * Abort a pending change using the token sent to the CURRENT address. This is
 * the safety net against a session-theft driven takeover.
 */
export async function cancelEmailChange(payload: { token: string }) {
  return post<{ data: { success: true; message: string } }>('/users/profile/email/change-cancel', {
    body: payload,
  });
}
